/**
 * Alarm helpers shared by the app-bar shield, the Alarm sheet and the security
 * dashboard: which panels a home has, how a state reads and is coloured, which
 * modes a panel supports, and which sensors are open before arming.
 */
import { isMotionClass, isPanicZone, type EntityMeta, type EntityState, type HomeModel, type ResolvedAccess } from '@home/shared';
import { AlarmFeature, features } from './entity.js';

type AccessLike = Pick<ResolvedAccess, 'isAdmin' | 'privileges'>;

/**
 * Known panels, most important first. Woodlands: house_alarm is the whole-house
 * panel, house/downstairs/upstairs/area_d the Texecom partitions. Flat: the
 * manual alarm. Keypad panels (NSPanel) are deliberately not listed.
 */
export const PANEL_ORDER = [
  'alarm_control_panel.house_alarm',
  'alarm_control_panel.house',
  'alarm_control_panel.downstairs',
  'alarm_control_panel.upstairs',
  'alarm_control_panel.area_d',
  'alarm_control_panel.alarm',
];

export interface AlarmPanels {
  /** The panel the shield shows and the big buttons drive. */
  primary: EntityMeta | null;
  /** Texecom partitions (rows in the sheet); empty when the home has one panel. */
  partitions: EntityMeta[];
  /** Other panels (keypads), shown only on the dashboard. */
  other: EntityMeta[];
}

const panelCache = new WeakMap<HomeModel, AlarmPanels>();

export function alarmPanels(model: HomeModel | undefined): AlarmPanels {
  if (!model) return { primary: null, partitions: [], other: [] };
  const hit = panelCache.get(model);
  if (hit) return hit;
  const all = Object.values(model.entities).filter((m) => m.domain === 'alarm_control_panel');
  const known = all.filter((m) => PANEL_ORDER.includes(m.id)).sort((a, b) => PANEL_ORDER.indexOf(a.id) - PANEL_ORDER.indexOf(b.id));
  let v: AlarmPanels;
  if (!known.length) {
    v = { primary: all[0] ?? null, partitions: [], other: all.slice(1) };
  } else {
    const [primary, ...rest] = known;
    // Texecom partitions only count as rows when there's more than one of them.
    const parts = rest.filter((m) => m.platform === 'texecom' || /^alarm_control_panel\.(house|downstairs|upstairs|area_[a-z])$/.test(m.id));
    v = { primary: primary!, partitions: parts.length > 1 ? parts : [], other: all.filter((m) => !known.includes(m)) };
  }
  panelCache.set(model, v);
  return v;
}

/** Shield visibility: the alarm_view privilege (or admin) in a home that has a panel. */
export function canSeeAlarm(model: HomeModel | undefined, access: AccessLike | undefined | null): boolean {
  if (!access || !alarmPanels(model).primary) return false;
  return access.isAdmin || access.privileges.includes('alarm_view');
}

export const canArm = (a: AccessLike | undefined | null): boolean => !!a && (a.isAdmin || a.privileges.includes('alarm_arm'));
export const canDisarm = (a: AccessLike | undefined | null): boolean => !!a && (a.isAdmin || a.privileges.includes('alarm_disarm'));

// ---------------------------------------------------------------- state → look

/** disarmed: neutral · armed: accent · pending: amber, pulsing · triggered: red, pulsing · unknown: dimmed. */
export type AlarmTone = 'disarmed' | 'armed' | 'pending' | 'triggered' | 'unknown';

export function alarmTone(s: string | undefined): AlarmTone {
  if (!s || s === 'unavailable' || s === 'unknown') return 'unknown';
  if (s === 'disarmed') return 'disarmed';
  if (s === 'triggered') return 'triggered';
  if (s === 'arming' || s === 'pending' || s === 'disarming') return 'pending';
  if (s.startsWith('armed')) return 'armed';
  return 'unknown';
}

const TONE_RANK: Record<AlarmTone, number> = { unknown: 0, disarmed: 1, armed: 2, pending: 3, triggered: 4 };

/** The shield's tone across the primary panel and its partitions: the most urgent wins. */
export function worstTone(states: Array<string | undefined>): AlarmTone {
  let best: AlarmTone = 'unknown';
  for (const s of states) {
    const t = alarmTone(s);
    if (TONE_RANK[t] > TONE_RANK[best]) best = t;
  }
  return best;
}

const LABEL: Record<string, string> = {
  disarmed: 'Disarmed',
  armed_home: 'Armed home',
  armed_away: 'Armed away',
  armed_night: 'Armed night',
  armed_vacation: 'Armed vacation',
  armed_custom_bypass: 'Armed custom',
  arming: 'Arming…',
  pending: 'Entry delay',
  disarming: 'Disarming…',
  triggered: 'Triggered',
  unavailable: 'Unavailable',
};
export const alarmLabel = (s: string | undefined): string => LABEL[s ?? 'unavailable'] ?? (s ? s.replace(/_/g, ' ') : 'Unavailable');

export const ALARM_ICON: Record<AlarmTone, string> = {
  disarmed: 'shield',
  armed: 'shield-check',
  pending: 'shield-pending',
  triggered: 'shield-alert',
  unknown: 'shield-off',
};

// ---------------------------------------------------------------- modes

export interface AlarmMode {
  service: string;
  label: string;
  state: string;
  icon: string;
}

const MODES: Array<AlarmMode & { bit: number }> = [
  { bit: AlarmFeature.ARM_HOME, service: 'alarm_arm_home', label: 'Home', state: 'armed_home', icon: 'home' },
  { bit: AlarmFeature.ARM_NIGHT, service: 'alarm_arm_night', label: 'Night', state: 'armed_night', icon: 'moon' },
  { bit: AlarmFeature.ARM_AWAY, service: 'alarm_arm_away', label: 'Away', state: 'armed_away', icon: 'shield-check' },
  { bit: AlarmFeature.ARM_VACATION, service: 'alarm_arm_vacation', label: 'Vacation', state: 'armed_vacation', icon: 'calendar' },
  { bit: AlarmFeature.ARM_CUSTOM_BYPASS, service: 'alarm_arm_custom_bypass', label: 'Custom', state: 'armed_custom_bypass', icon: 'sliders' },
];

/** Modes the owner doesn't use; never offered even if the panel advertises them. */
const HIDDEN_MODES = new Set(['alarm_arm_home', 'alarm_arm_night', 'alarm_arm_vacation']);

/** Arm modes the panel advertises (supported_features), minus HIDDEN_MODES; Away when it advertises none. */
export function alarmModes(st: EntityState | undefined): AlarmMode[] {
  const f = features(st);
  const list = MODES.filter((m) => (f & m.bit) !== 0 && !HIDDEN_MODES.has(m.service));
  return (list.length ? list : MODES.filter((m) => m.service === 'alarm_arm_away')).map(({ bit: _bit, ...m }) => m);
}

// ---------------------------------------------------------------- sensors

const SENSOR_CLASSES = new Set(['motion', 'occupancy', 'door', 'opening', 'window', 'presence', 'garage_door']);

const systemOf = (model: HomeModel, m: EntityMeta): string | null => (m.deviceId ? (model.devices[m.deviceId]?.system ?? null) : null);

/** Door, window and PIR sensors that belong to the alarm (Texecom zones, or the Security system). */
export function securitySensors(model: HomeModel | undefined): EntityMeta[] {
  if (!model) return [];
  return Object.values(model.entities).filter(
    (m) =>
      m.domain === 'binary_sensor' &&
      m.category === null &&
      m.deviceClass !== null &&
      SENSOR_CLASSES.has(m.deviceClass) &&
      (systemOf(model, m) === 'Security' || m.platform === 'texecom'),
  );
}

/**
 * The alarm's own zones: security sensors that aren't camera detections
 * (Frigate/Reolink/Tapo motion and object occupancy sit on camera devices and
 * don't arm with the panel). These are what the pre-arm warning checks.
 */
export function alarmZones(model: HomeModel | undefined): EntityMeta[] {
  if (!model) return [];
  const cameraDevices = new Set(
    Object.values(model.entities)
      .filter((m) => m.domain === 'camera' && m.deviceId)
      .map((m) => m.deviceId!),
  );
  return securitySensors(model).filter((m) => m.platform !== 'frigate' && !(m.deviceId && cameraDevices.has(m.deviceId)));
}

// Zone classification lives in @home/shared (the server's maintenance PIR check uses it too).
export { isMotionClass, isPanicZone };

/** The alarm's panic buttons (any device class): Security-system or Texecom binary sensors whose id/name says "panic". */
export function panicButtons(model: HomeModel | undefined): EntityMeta[] {
  if (!model) return [];
  return Object.values(model.entities).filter(
    (m) => m.category === null && isPanicZone(m) && (systemOf(model, m) === 'Security' || m.platform === 'texecom'),
  );
}

/** Sensors that would trip or block arming right now: open doors/windows and active PIRs. */
export function openSensors(sensors: EntityMeta[], stateOf: (id: string) => EntityState | undefined): EntityMeta[] {
  return sensors.filter((m) => stateOf(m.id)?.s === 'on');
}

// ---------------------------------------------------------------- sensor labels

/** Panel/integration prefixes and "what it is" words that say nothing about where a PIR is. */
const SENSOR_NOISE = /\b(?:texecom(?:\s+premier)?(?:\s+elite)?(?:\s+\d+)?|motion|occupancy|presence|pir|sensor|detector)\b/gi;

const upperFirst = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** "Texecom Premier Elite 64 Kitchen PIR" → "Kitchen". Empty when nothing but noise is left. */
export function cleanSensorName(name: string): string {
  return upperFirst(name.replace(SENSOR_NOISE, ' ').replace(/[\s·:_-]+/g, ' ').trim());
}

/** Ways a room's name is written at the front of a sensor name ("Kitchen & Living Room" → also "Kitchen", "Living Room", "Living"). */
function roomAliases(room: string): string[] {
  const out = new Set([room]);
  for (const part of room.split(/\s*(?:&|\/|\band\b)\s*/i).map((p) => p.trim()).filter(Boolean)) {
    out.add(part);
    if (/\sroom$/i.test(part)) out.add(part.replace(/\s+room$/i, ''));
    // "Master Bedroom" → "Bedroom", "Downstairs Loo" → "Loo".
    if (/^(?:master|downstairs|upstairs|ground|first|top)\s+\S/i.test(part)) out.add(part.replace(/^\S+\s+/, ''));
  }
  return [...out].sort((a, b) => b.length - a.length);
}

/** What a name says beyond its room: "Top of stairs" stays, "Dressing room" (in Dressing Room) → "". */
function beyondRoom(clean: string, room: string): string {
  for (const a of roomAliases(room)) {
    if (clean.toLowerCase() === a.toLowerCase()) return '';
    if (clean.toLowerCase().startsWith(`${a.toLowerCase()} `)) return clean.slice(a.length).trim();
  }
  return clean;
}

/** What an entity id adds beyond its room; "" for an id that is just the room or its abbreviation (`binary_sensor.hall` in Hallway). */
function idExtra(id: string, room: string): string {
  const extra = beyondRoom(cleanSensorName(id.slice(id.indexOf('.') + 1).replace(/_/g, ' ')), room);
  const squash = (t: string): string => t.toLowerCase().replace(/[^a-z0-9]/g, '');
  return squash(room).startsWith(squash(extra)) ? '' : extra;
}

/** Camera motion/object detections (Frigate, or a binary_sensor on a camera's device): named after the camera, not the room. */
function cameraDetection(model: HomeModel): (m: EntityMeta) => boolean {
  const cams = new Set(Object.values(model.entities).filter((m) => m.domain === 'camera' && m.deviceId).map((m) => m.deviceId!));
  return (m) => m.platform === 'frigate' || (!!m.deviceId && cams.has(m.deviceId));
}

/**
 * Security-dashboard labels (owner: "name the motion sensors after the rooms,
 * not the entity"). Alarm PIRs read as their room ("Peloton" → "Gym",
 * "Texecom Premier Elite 64 Kitchen panic" → "Kitchen"). A policy label that
 * says more than its room wins, minus the noise words ("Top of stairs motion" →
 * "Top of stairs"). Two PIRs in one room: "Room · <what the name/id adds>"
 * when they differ, else "Room", "Room 2". A PIR with no room gets its cleaned
 * name. Doors, windows and camera detections keep their names.
 */
export function securitySensorLabels(model: HomeModel | undefined, sensors: EntityMeta[]): Map<string, string> {
  const out = new Map<string, string>();
  if (!model) return out;
  const roomName = new Map(model.rooms.map((r) => [r.id, r.name]));
  const isCam = cameraDetection(model);
  const byRoom = new Map<string, Array<{ m: EntityMeta; extra: string }>>();
  const panics = sensors.filter(isPanicZone);
  for (const [id, l] of panicButtonLabels(model, panics)) out.set(id, l);
  for (const m of sensors) {
    if (isPanicZone(m)) continue;
    const room = m.roomId ? roomName.get(m.roomId) : undefined;
    if (!isMotionClass(m.deviceClass) || isCam(m)) out.set(m.id, m.name);
    else if (!room) out.set(m.id, cleanSensorName(m.name) || m.name);
    else {
      const clean = cleanSensorName(m.name);
      const extra = beyondRoom(clean, room);
      if (m.labelled && extra) out.set(m.id, clean);
      else {
        let l = byRoom.get(room);
        if (!l) byRoom.set(room, (l = []));
        // Only to tell two PIRs in a room apart: what the name adds, else what the entity id adds ("…_dressing_room_panic" → "panic").
        l.push({ m, extra: extra || idExtra(m.id, room) });
      }
    }
  }
  // Names other PIRs/doors already use (panic buttons live in their own section, so they don't count).
  const taken = new Set([...out].filter(([id]) => !panics.some((p) => p.id === id)).map(([, v]) => v.toLowerCase()));
  for (const [room, list] of byRoom) {
    if (list.length === 1 && !taken.has(room.toLowerCase())) {
      out.set(list[0]!.m.id, room);
      continue;
    }
    // Plainest first (the one whose name is just the room keeps it), then by id for stability.
    list.sort((a, b) => a.extra.length - b.extra.length || a.m.id.localeCompare(b.m.id));
    const extras = list.map((x) => x.extra.toLowerCase());
    const unique = (x: { extra: string }): boolean => !!x.extra && extras.filter((e) => e === x.extra.toLowerCase()).length === 1;
    // Several PIRs whose names each say something different: all get a suffix; otherwise the plain ones are numbered.
    let n = taken.has(room.toLowerCase()) ? 1 : 0;
    const allUnique = list.every(unique);
    for (const x of list) {
      if (unique(x) && (allUnique || n > 0)) out.set(x.m.id, `${room} · ${x.extra.toLowerCase()}`);
      else out.set(x.m.id, (n += 1) === 1 ? room : `${room} ${n}`);
    }
  }
  return out;
}

/** Panic buttons read as their room ("Dressing Room"); a second one in a room is numbered; no room → name minus panel/"panic" words. */
export function panicButtonLabels(model: HomeModel | undefined, buttons: EntityMeta[]): Map<string, string> {
  const out = new Map<string, string>();
  const roomName = new Map((model?.rooms ?? []).map((r) => [r.id, r.name]));
  const seen = new Map<string, number>();
  for (const m of [...buttons].sort((a, b) => a.id.localeCompare(b.id))) {
    const room = m.roomId ? roomName.get(m.roomId) : undefined;
    const base = room ?? (cleanSensorName(m.name.replace(/\bpanic(?:\s+button)?\b/gi, ' ')) || m.name);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    out.set(m.id, n === 1 ? base : `${base} ${n}`);
  }
  return out;
}
